/**
 * EvolutionChart — accessible time-series display for weight/body measures.
 *
 * Security (CLAUDE.md §4):
 *   All user-sourced strings rendered via React text nodes — NEVER dangerouslySetInnerHTML.
 *
 * Accessibility (CLAUDE.md §7, spec tracking-dashboard):
 *   - Semantic HTML: section > h2 + table (text alternative to visual chart)
 *   - aria-labelledby on section points to heading id
 *   - Table has aria-label for screen reader context
 *   - No color-only signaling (WCAG 2.1 AA)
 *   - Loading: role="status" with sr-only text
 *   - Error: role="alert" with visible message
 *   - Empty: visible text message, no broken chart
 *
 * States: loading / error / empty / data.
 */

import type { EvolutionPoint } from "@autodieta/shared";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EvolutionChartProps {
  /** Time-series evolution entries. */
  points?: EvolutionPoint[];
  /** When true, renders loading skeleton. */
  loading?: boolean;
  /** When set, renders error state. */
  error?: string;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HEADING_ID = "evolution-heading";

export function EvolutionChart({ points, loading, error }: EvolutionChartProps): JSX.Element {
  if (loading) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Evolución
        </h2>
        <div
          role="status"
          aria-label="Cargando datos de evolución"
          className="flex flex-col gap-3 rounded-xl border border-zinc-700 bg-zinc-900 p-4"
        >
          <div className="h-4 w-48 animate-pulse rounded bg-zinc-700" />
          <div className="h-4 w-full animate-pulse rounded bg-zinc-800" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-zinc-800" />
          <span className="sr-only">Cargando datos de evolución…</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Evolución
        </h2>
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-xl border border-red-800/60 bg-red-950/20 px-4 py-3 text-sm text-red-400"
        >
          {error}
        </div>
      </section>
    );
  }

  if (!points || points.length === 0) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Evolución
        </h2>
        <p className="py-4 text-center text-sm text-zinc-500">
          Registrá tu primer peso para ver tu evolución.
        </p>
      </section>
    );
  }

  // Sort ascending by createdAt
  const sorted = [...points].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return (
    <section aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
        Evolución
      </h2>
      <div className="overflow-x-auto rounded-xl border border-zinc-700 bg-zinc-900">
        <table
          aria-label="Registros de evolución de peso y medidas"
          className="min-w-full text-sm"
        >
          <caption className="sr-only">
            Historial de evolución de peso y medidas corporales, ordenado por fecha ascendente.
          </caption>
          <thead>
            <tr className="border-b border-zinc-700">
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-400"
              >
                Fecha
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-400"
              >
                Peso (kg)
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-400"
              >
                % Grasa
              </th>
              <th
                scope="col"
                className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-400"
              >
                Medidas
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((point) => (
              <EvolutionRow key={point.id} point={point} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function EvolutionRow({ point }: { point: EvolutionPoint }): JSX.Element {
  const date = formatDate(point.createdAt);
  const measures =
    point.measures != null
      ? Object.entries(point.measures)
          .map(([k, v]) => `${k}: ${v}`)
          .join(", ")
      : null;

  return (
    <tr className="border-b border-zinc-800 last:border-0">
      <td className="px-4 py-2 text-zinc-300">{date}</td>
      <td className="px-4 py-2 text-right text-zinc-100">
        {point.weightKg != null ? `${point.weightKg}` : <span className="text-zinc-500">—</span>}
      </td>
      <td className="px-4 py-2 text-right text-zinc-300">
        {point.bodyFatPct != null ? `${point.bodyFatPct}%` : <span className="text-zinc-500">—</span>}
      </td>
      <td className="px-4 py-2 text-zinc-400">
        {measures ?? <span className="text-zinc-600">—</span>}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}
